import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import {
  IProductSpeedAnalysisDownloadExcelWorkOrderDetails,
  IProductSpeedAnalysisDownloadExcelBayesianAnalysis,
  ProductSpeedAnalysisInterface,
  SetProductSpeedInterface,
  SetProductSpeedResponseInterface,
} from '../../../store/reports/product-speed-analysis/product-speed-analysis.model';
import { GetManyResponseInterface } from '../../model/interface/crud-response-interface.model';
import { AddProductSpeedResponseInterface } from '../../../store/settings/product-speeds/product-speeds.model';
import { TranslateService } from '@ngx-translate/core';
import { ICreateExcelSheet, IExcelColumnDefinition } from '../excel/excel.helper';
import { EExcelColumnWidth, EExcelSheetType } from '../excel/excel.enum';
import { ValueType } from 'exceljs';
import { ExcelHelperService } from '../excel/excel.helper.service';
import * as ObjectActions from '../../../store/reports/product-speed-analysis/product-speed-analysis.actions';
import { Store } from '@ngrx/store';
import { OeeAppState } from '../../../store/oee.reducer';
import { DecimalHelper } from '../../helper/decimal/decimal-helper';
import moment from 'moment';
import { takeUntil } from 'rxjs/operators';
import { excelDateFormat, excelTimeFormat } from '../../model/enum/excel-date-format';

@Injectable({
  providedIn: 'root',
})
export class ProductSpeedAnalysisService {
  constructor(
    public http: HttpClient,
    @Inject('API_BASE_URL') private readonly api: string,
    private readonly translate: TranslateService,
    private excelHelper: ExcelHelperService,
    private readonly store: Store<OeeAppState>,
    private readonly decimalHelper: DecimalHelper,
  ) {
    const destroySubject: Subject<boolean> = new Subject<boolean>();
    this.store
      .select('user')
      .pipe(takeUntil(destroySubject))
      .subscribe((state) => {
        if (state.isUserLoaded) {
          this.timezone = state.timezone;

          if (state.locale !== '') {
            this.dateFormat$ = excelDateFormat[state.locale];
          }

          destroySubject.next(true);
          destroySubject.complete();
        }
      });
  }

  private readonly routes = {
    productSpeedAnalysis: `${this.api}/product-speeds/product-speed-analysis`,
    setProductSpeed: `${this.api}/product-speeds/product-speed/save`,
  };

  private timezone: string = 'utc';
  private dateFormat$: string;

  public productSpeedAnalysisDataLoading(
    params: HttpParams,
  ): Observable<GetManyResponseInterface<ProductSpeedAnalysisInterface>> {
    return this.http.get<GetManyResponseInterface<ProductSpeedAnalysisInterface>>(this.routes.productSpeedAnalysis, {
      params,
    });
  }

  public setProductSpeed(productSpeed: SetProductSpeedInterface): Observable<AddProductSpeedResponseInterface> {
    return this.http.post<SetProductSpeedResponseInterface>(`${this.routes.setProductSpeed}`, productSpeed);
  }

  public static setProductSpeedAnalysisProductName(productSpeedAnalysis: ProductSpeedAnalysisInterface): void {
    productSpeedAnalysis.productName = `${productSpeedAnalysis.productId} - ${productSpeedAnalysis.productDescription}`;
  }

  private prepareWorkOrderDetailsSheetData(
    data: ProductSpeedAnalysisInterface[],
    countType: string,
  ): IProductSpeedAnalysisDownloadExcelWorkOrderDetails[] {
    return data.flatMap((item) =>
      item.originalData.map((workOrder) => ({
        siteName: item.siteName ?? '',
        productId: item.productId,
        lineName: item.lineName,
        countType: this.translate.instant('waterfallAnalysis.excel.countType.' + countType),
        productDescription: item.productDescription ?? '',
        woNumber: workOrder.woNumber,
        quantityOrdered: workOrder.quantityOrdered
          ? this.decimalHelper.toFixedValue(workOrder.quantityOrdered, 2)
          : '0',
        duration: workOrder.OEETotalDuration
          ? this.decimalHelper.toFixedValue(String(workOrder.OEETotalDuration / 60), 2)
          : '0',
        summarySpeed: workOrder.summarySpeed
          ? this.decimalHelper.toFixedValue(String(workOrder.summarySpeed), 2)
          : '0',
        catalogSpeed: item.catalogSpeed ?
          this.decimalHelper.toFixedValue(String(item.catalogSpeed), 2) +
          ' ' +
          this.translate.instant('products.lookups.unit.' + item.productUnit + '.formula') : '0',
      })),
    );
  }

  private prepareBayesianAnalysisSheetData(
    data: ProductSpeedAnalysisInterface[],
    countType: string,
  ): IProductSpeedAnalysisDownloadExcelBayesianAnalysis[] {
    return data.map((item) => ({
      siteName: item.siteName,
      lineName: item.lineName,
      countType: this.translate.instant('waterfallAnalysis.excel.countType.' + countType),
      productId: item.productId,
      productDescription: item.productDescription ?? '',
      observedSpeed: item.observedSpeed ? this.decimalHelper.toFixedValue(item.observedSpeed, 2) : '0',
      confidence: `${String(item.conf)}%`,
      catalogSpeed: item.catalogSpeed ?
        this.decimalHelper.toFixedValue(String(item.catalogSpeed), 2) +
        ' ' +
        this.translate.instant('products.lookups.unit.' + item.productUnit + '.formula') : '0',
    }));
  }

  private getWorkOrderDetailColumns(): IExcelColumnDefinition[] {
    return [
      {
        header: this.translate.instant('waterfallAnalysis.excel.siteName.header'),
        key: 'siteName',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },
      {
        header: this.translate.instant('waterfallAnalysis.excel.lineName.header'),
        key: 'lineName',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },
      {
        header: this.translate.instant('waterfallAnalysis.excel.productId.header'),
        key: 'productId',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },
      {
        header: this.translate.instant('waterfallAnalysis.excel.productDescription.header'),
        key: 'productDescription',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },
      {
        header: this.translate.instant('waterfallAnalysis.excel.workOrder.header'),
        key: 'woNumber',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },

      {
        header: this.translate.instant('waterfallAnalysis.excel.quantityOrdered.header'),
        key: 'quantityOrdered',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },
      {
        header: this.translate.instant('waterfallAnalysis.excel.countType.header'),
        key: 'countType',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },
      {
        header: this.translate.instant('waterfallAnalysis.excel.duration.header'),
        key: 'duration',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },
      {
        header: this.translate.instant('waterfallAnalysis.excel.summarySpeed.header'),
        key: 'summarySpeed',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },
      {
        header: this.translate.instant('waterfallAnalysis.excel.catalogSpeed.header'),
        key: 'catalogSpeed',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },
    ];
  }

  private getBayesianAnalysisColumns(): IExcelColumnDefinition[] {
    return [
      {
        header: this.translate.instant('waterfallAnalysis.excel.siteName.header'),
        key: 'siteName',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },
      {
        header: this.translate.instant('waterfallAnalysis.excel.lineName.header'),
        key: 'lineName',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },
      {
        header: this.translate.instant('waterfallAnalysis.excel.productId.header'),
        key: 'productId',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },
      {
        header: this.translate.instant('waterfallAnalysis.excel.productDescription.header'),
        key: 'productDescription',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },

      {
        header: this.translate.instant('waterfallAnalysis.excel.observedSpeed.header'),
        key: 'observedSpeed',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },
      {
        header: this.translate.instant('waterfallAnalysis.excel.confidence.header'),
        key: 'confidence',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },
      {
        header: this.translate.instant('waterfallAnalysis.excel.catalogSpeed.header'),
        key: 'catalogSpeed',
        width: EExcelColumnWidth.DEFAULT,
        type: ValueType.String,
        style: { numFmt: '@' },
        dataValidation: null,
      },
    ];
  }

  private prepareExcel(
    workOrderDetailData: IProductSpeedAnalysisDownloadExcelWorkOrderDetails[],
    bayesianAnalysisData: IProductSpeedAnalysisDownloadExcelBayesianAnalysis[],
    siteId: number,
  ): void {
    const worksheets: ICreateExcelSheet[] = [
      {
        sheetTitle: this.translate.instant('waterfallAnalysis.excel.sheet.workOrderDetails'),
        sheetType: EExcelSheetType.TABLE,
        params: {
          columns: this.getWorkOrderDetailColumns(),
          data: workOrderDetailData,
        },
        withData: true,
        isDisabledColumnsFirstLine: true,
        addDateTimeFormula: undefined,
      },
      {
        sheetTitle: this.translate.instant('waterfallAnalysis.excel.sheet.bayesianAnalysis'),
        sheetType: EExcelSheetType.TABLE,
        params: {
          columns: this.getBayesianAnalysisColumns(),
          data: bayesianAnalysisData,
        },
        withData: true,
        isDisabledColumnsFirstLine: true,
        addDateTimeFormula: undefined,
      },
    ];

    const pageTitle: string = this.translate.instant('pageTitles.productSpeedAnalysis');
    const excelName: string = `${pageTitle} ${moment().tz(this.timezone).format(this.dateFormat$)}`;

    this.excelHelper
      .createExcel(
        excelName,
        {
          name: 'productSpeedAnalysis',
          withData: true,
          siteId: siteId,
        },
        worksheets,
        this.timezone,
        this.dateFormat$,
      )
      .then(
        () => {
          this.store.dispatch(new ObjectActions.ProductSpeedAnalysisDownloadExcelCompleted());
        },
        () => {
          this.store.dispatch(new ObjectActions.FetchError(null));
        },
      );
  }

  public downloadExcel(tableData: ProductSpeedAnalysisInterface[], countType: string, siteId: number): void {
    const workOrderDetailsData: IProductSpeedAnalysisDownloadExcelWorkOrderDetails[] =
      this.prepareWorkOrderDetailsSheetData(tableData, countType);
    const bayesianAnalysesData: IProductSpeedAnalysisDownloadExcelBayesianAnalysis[] =
      this.prepareBayesianAnalysisSheetData(tableData, countType);

    this.prepareExcel(workOrderDetailsData, bayesianAnalysesData, siteId);
  }
}
